import React, { useState, useEffect } from "react";
import Overview from "./Overview";
import EmployeeForm from "./EmployeeForm";
import EmployeesList from "./EmployeesList";
import EmployeeView from "./EmployeeView";
import Cashbook from "../components/Cashbook";
import Attendance from "./Attendance";
import Users from "./Users";
import SalaryDash from "./SalaryDash";
import Advance from "./Advance";
import AdminNotes from "./AdminNotes";
import Invoices from "./Invoices";
import Profile from "./Profile";
import ServiceScheduler from "../components/ServiceScheduler";
import PartStock from "../components/PartStock";
import Appointments from "./Appointments";
import TrainingWelfare from "./TrainingWelfare";
import DocumentVault from "./DocumentVault";
import GSTFiling from "./GSTFiling";
import { toast } from "react-toastify";

import ACAudit from "./ACAudit";
import { useSearchParams } from "react-router-dom";

export default function AdminDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "dashboard");
  const [selectedEmpId, setSelectedEmpId] = useState(null);
  const [editEmp, setEditEmp] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setTab(t);
  }, [searchParams]);

  const resetSubViews = () => {
    setSelectedEmpId(null);
    setEditEmp(null);
    setShowAdd(false);
  };

  const allTabs = ["dashboard", "cashbook", "Appointments", "Billing", "GST Filing", "Service Scheduler", "Document Vault", "employees", "Attendance", "Salary", "PartStock", "Training & Welfare", "Audit", "notes", "Users", "Profile"];
  const primaryTabs = ["dashboard", "cashbook", "employees", "Attendance", "Salary", "Audit"];
  const moreTabs = ["Appointments", "Billing", "GST Filing", "Service Scheduler", "Document Vault", "PartStock", "Training & Welfare", "notes", "Users", "Profile"];

  return (
    <div className="container-fluid px-4">

      <style>{`
        @media (max-width: 1600px) {
          .desktop-tabs-view { display: none !important; }
          .laptop-tabs-view { display: flex !important; }
        }
        @media (min-width: 1601px) {
          .desktop-tabs-view { display: flex !important; }
          .laptop-tabs-view { display: none !important; }
        }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* DESKTOP TABS: Show all, wrap if needed (Show only on ultra-wide screens) */}
      <ul className="nav nav-pills mt-3 mb-4 p-2 bg-light rounded-4 desktop-tabs-view shadow-sm border flex-wrap">
        {allTabs.map(t => (
          <li className="nav-item" key={t}>
            <button
              className={`nav-link ${tab === t ? "active fw-bold shadow-sm" : "text-secondary"}`}
              onClick={() => {
                setTab(t);
                setSearchParams({ tab: t });
                resetSubViews();
              }}
              style={{
                borderRadius: "20px",
                margin: "4px",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                padding: "8px 20px",
                fontSize: "0.9rem",
                whiteSpace: "nowrap"
              }}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          </li>
        ))}
      </ul>

      {/* MOBILE & TABLET & LAPTOP TABS (Up to 14-inch): Show Primary + More Dropdown */}
      <div className="laptop-tabs-view mt-3 mb-4 p-1 bg-light rounded-pill shadow-sm border align-items-center">
        <div className="flex-grow-1 overflow-hidden">
          <ul className="nav nav-pills flex-nowrap hide-scrollbar"
            style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            {primaryTabs.map(t => (
              <li className="nav-item" key={t}>
                <button
                  className={`nav-link ${tab === t ? "active fw-bold shadow-sm" : "text-secondary"}`}
                  onClick={() => {
                    setTab(t);
                    setSearchParams({ tab: t });
                    resetSubViews();
                  }}
                  style={{
                    borderRadius: "20px",
                    margin: "0 4px",
                    padding: "8px 18px",
                    fontSize: "0.85rem",
                    whiteSpace: "nowrap"
                  }}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="dropdown border-start ms-2 ps-1">
          <button
            className={`nav-link dropdown-toggle ${moreTabs.includes(tab) ? "active fw-bold shadow-sm" : "text-secondary"}`}
            data-bs-toggle="dropdown"
            aria-expanded="false"
            style={{
              borderRadius: "20px",
              padding: "8px 15px",
              fontSize: "0.85rem",
              whiteSpace: "nowrap"
            }}
          >
            {moreTabs.includes(tab) ? tab.charAt(0).toUpperCase() + tab.slice(1) : "More"}
          </button>
          <ul className="dropdown-menu dropdown-menu-end shadow border-0 mt-2" style={{ borderRadius: "15px", zIndex: 2000 }}>
            {moreTabs.map(t => (
              <li key={t}>
                <button
                  className={`dropdown-item py-2 px-4 ${tab === t ? "bg-primary text-white fw-bold" : ""}`}
                  style={{ fontSize: "0.9rem" }}
                  onClick={() => {
                    setTab(t);
                    setSearchParams({ tab: t });
                    resetSubViews();
                  }}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-2">
        {tab === "dashboard" && <Overview />}
        {tab === "Appointments" && <Appointments />}
        {tab === "cashbook" && <Cashbook />}
        {tab === "Billing" && <Invoices />}
        {tab === "GST Filing" && <GSTFiling />}
        {tab === "Attendance" && <Attendance />}
        {tab === "Users" && (
          <Users
            onEdit={(user) => {
              setSelectedUser(user);
              toast.info("User editing is coming soon. Use Profile tab for now.");
            }}
            onAdd={() => toast.info("To add a user, use the Employees tab with a UID.")}
          />
        )}
        {tab === "Salary" && <SalaryDash />}
        {tab === "Advance" && <Advance />}
        {tab === "Service Scheduler" && <ServiceScheduler />}
        {tab === "Document Vault" && <DocumentVault />}
        {tab === "employees" && (
          <div className="mt-3">
            {!selectedEmpId && !editEmp && !showAdd ? (
              <EmployeesList
                onView={(id) => setSelectedEmpId(id)}
                onEdit={(id) => setEditEmp(id)}
                onAdd={() => setShowAdd(true)}
              />
            ) : showAdd ? (
              <EmployeeForm onBack={resetSubViews} />
            ) : editEmp ? (
              <EmployeeForm id={editEmp} onBack={resetSubViews} />
            ) : (
              <EmployeeView id={selectedEmpId} onBack={resetSubViews} />
            )}
          </div>
        )}
        {tab === "PartStock" && <PartStock />}
        {tab === "Training & Welfare" && <TrainingWelfare />}
        {tab === "Audit" && <ACAudit onBack={() => setTab("dashboard")} />}
        {tab === "notes" && <AdminNotes />}
        {tab === "Profile" && <Profile />}

      </div>
    </div>
  );
}
